/**
 * DropdownCell Component
 *
 * Renders single or multi-select dropdown options.
 * Uses headless UI for accessible dropdown menu.
 */

import { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { DropdownCellProps } from './types';

export function DropdownCell({
  value,
  onChange,
  options = [],
  multiple = false,
  readonly = false,
}: DropdownCellProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedIds = value?.selectedIds ?? [];

  // Get selected options details
  const selectedOptions = selectedIds
    .map(id => options.find(o => o.id === id))
    .filter(Boolean) as NonNullable<typeof options[0]>[];

  const handleSelect = (optionId: string) => {
    if (readonly || !onChange) return;

    if (multiple) {
      const newIds = selectedIds.includes(optionId)
        ? selectedIds.filter(id => id !== optionId)
        : [...selectedIds, optionId];
      onChange(newIds.length > 0 ? { selectedIds: newIds } : null);
    } else {
      if (selectedIds.includes(optionId)) {
        onChange(null);
      } else {
        onChange({ selectedIds: [optionId] });
      }
      setIsOpen(false);
    }
  };

  const handleRemove = (optionId: string) => {
    if (readonly || !onChange) return;
    const newIds = selectedIds.filter(id => id !== optionId);
    onChange(newIds.length > 0 ? { selectedIds: newIds } : null);
  };

  if (options.length === 0) {
    return (
      <div className="w-full h-full flex items-center">
        <span className="text-gray-400 text-sm italic">No options</span>
      </div>
    );
  }

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center gap-1 flex-wrap overflow-hidden">
        {selectedOptions.length > 0 ? (
          selectedOptions.map(option => (
            <span
              key={option.id}
              className="px-2 py-0.5 text-xs font-medium rounded"
              style={{
                backgroundColor: option.color ? `${option.color}20` : '#f3f4f6',
                color: option.color || '#374151',
              }}
            >
              {option.label}
            </span>
          ))
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <Listbox value={selectedIds} onChange={() => {}} multiple={multiple}>
      <div className="relative w-full">
        <Listbox.Button
          className="w-full h-full flex items-center justify-between cursor-pointer min-h-[28px]"
          onClick={() => setIsOpen(true)}
        >
          <div className="flex items-center gap-1 flex-wrap overflow-hidden flex-1">
            {selectedOptions.length > 0 ? (
              multiple ? (
                selectedOptions.slice(0, 2).map(option => (
                  <span
                    key={option.id}
                    className="px-2 py-0.5 text-xs font-medium rounded"
                    style={{
                      backgroundColor: option.color ? `${option.color}20` : '#f3f4f6',
                      color: option.color || '#374151',
                    }}
                  >
                    {option.label}
                  </span>
                ))
              ) : selectedOptions[0] ? (
                <span
                  className="px-2 py-0.5 text-xs font-medium rounded"
                  style={{
                    backgroundColor: selectedOptions[0].color ? `${selectedOptions[0].color}20` : '#f3f4f6',
                    color: selectedOptions[0].color || '#374151',
                  }}
                >
                  {selectedOptions[0].label}
                </span>
              ) : null
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
            {multiple && selectedOptions.length > 2 && (
              <span className="text-xs text-gray-500">
                +{selectedOptions.length - 2}
              </span>
            )}
          </div>
          <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </Listbox.Button>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setIsOpen(false)}
        >
          <div className="absolute z-50 mt-1 w-52 bg-white rounded-lg shadow-dropdown border border-surface-border overflow-hidden">
            {/* Selected options (for multi-select) */}
            {multiple && selectedOptions.length > 0 && (
              <div className="p-2 border-b border-surface-border">
                <div className="flex flex-wrap gap-1">
                  {selectedOptions.map((option) => (
                    <span
                      key={option.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                      style={{
                        backgroundColor: option.color ? `${option.color}20` : '#f3f4f6',
                        color: option.color || '#374151',
                      }}
                    >
                      {option.label}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(option.id);
                        }}
                        className="hover:opacity-70"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Options list */}
            <Listbox.Options static className="max-h-48 overflow-auto py-1">
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-3 ${
                      active ? 'bg-surface-hover' : ''
                    }`
                  }
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded"
                      style={{
                        backgroundColor: option.color ? `${option.color}20` : '#f3f4f6',
                        color: option.color || '#374151',
                      }}
                    >
                      {option.label}
                    </span>
                    {selectedIds.includes(option.id) && (
                      <CheckIcon className="h-4 w-4 text-primary-500" />
                    )}
                  </div>
                </Listbox.Option>
              ))}
            </Listbox.Options>

            {/* Done button (for multi-select) */}
            {multiple && (
              <div className="border-t border-surface-border p-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full px-3 py-1.5 text-sm text-gray-600 hover:bg-surface-hover rounded"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </Transition>
      </div>
    </Listbox>
  );
}
